import React, { useMemo, useState } from 'react';
import { Input, Select, Typography, InputNumber } from 'antd';
import { useTranslation } from 'react-i18next';
import { 
  getManufacturers,
  getModelSeriesByManufacturer,
  getEngineTypes,
  type EngineBaseType
} from '@/shared/lib/vehicle-data';
import { useGetApiVehicleLookupRegistrationNumber } from '@/features/guides/api/generated';

const { Text } = Typography;
const { Search } = Input;

export interface VehicleSelectionValue {
  manufacturer?: string;
  modelSeries?: string;
  engineBaseType?: EngineBaseType;
  euroCode?: string;
  modelYear?: number;
  modelYearMin?: number;
  modelYearMax?: number;
}

interface VehicleEnrichmentProps {
  value: VehicleSelectionValue;
  onChange: (next: VehicleSelectionValue) => void;
  yearMode?: 'single' | 'range'; // 'single' for modelYear, 'range' for min/max
}

export const VehicleEnrichment: React.FC<VehicleEnrichmentProps> = ({ value, onChange, yearMode = 'single' }) => {
  const { t } = useTranslation();
  const [reg, setReg] = useState('');

  const { refetch: lookup, isFetching } = useGetApiVehicleLookupRegistrationNumber(reg || '', {
    query: {
      enabled: false,
    },
  });

  const manufacturers = useMemo(() => getManufacturers(), []);
  const modelSeries = useMemo(() => value.manufacturer ? getModelSeriesByManufacturer(value.manufacturer) : [], [value.manufacturer]);
  const engineTypes = useMemo(() => {
    let availableTypes: string[] = [];
    
    // If both manufacturer and model series are selected, show specific engine types
    if (value.manufacturer && value.modelSeries) {
      const list = getEngineTypes(value.manufacturer, value.modelSeries);
      availableTypes = list.filter(t => t !== 'Unknown');
    } else {
      // Otherwise, show all available engine types from all manufacturers/models
      const allEngineTypes = new Set<string>();
      manufacturers.forEach(manufacturer => {
        const models = getModelSeriesByManufacturer(manufacturer);
        models.forEach(model => {
          const engines = getEngineTypes(manufacturer, model);
          engines.forEach(engine => {
            if (engine !== 'Unknown') {
              allEngineTypes.add(engine);
            }
          });
        });
      });
      availableTypes = Array.from(allEngineTypes).sort();
    }
    
    // If current value is not in available types, add it temporarily so user can see and clear it
    if (value.engineBaseType && !availableTypes.includes(value.engineBaseType)) {
      availableTypes = [value.engineBaseType, ...availableTypes];
    }
    
    return availableTypes;
  }, [value.manufacturer, value.modelSeries, value.engineBaseType, manufacturers]);

  const setField = (field: keyof VehicleSelectionValue, v: unknown) => {
    // Convert null and empty strings to undefined to properly clear values
    // Keep 0 for modelYear as it could be a valid value
    const cleanValue = (v === null || v === '') ? undefined : v;
    const next: VehicleSelectionValue = { ...value, [field]: cleanValue } as VehicleSelectionValue;
    // Reset dependent fields - only reset modelSeries when manufacturer changes
    // Engine type can now be selected independently
    if (field === 'manufacturer') {
      next.modelSeries = undefined;
    }
    onChange(next);
  };

  const onLookup = async () => {
    if (!reg) return;
    const res = await lookup();
    const data = res.data;
    if (!data) return;
    
    const baseUpdate = {
      manufacturer: (data.brand as string | undefined) ?? value.manufacturer,
      modelSeries: (data.model as string | undefined) ?? value.modelSeries,
      engineBaseType: ((data.engineTypes && data.engineTypes[0]) as EngineBaseType | undefined) ?? value.engineBaseType,
      euroCode: (data.eurocode as string | undefined) ?? value.euroCode,
    };
    
    if (yearMode === 'single') {
      onChange({
        ...baseUpdate,
        modelYear: (data.modelYear as number | undefined) ?? value.modelYear,
      });
    } else {
      // For range mode, set both min and max to the looked up year
      const lookedUpYear = (data.modelYear as number | undefined);
      onChange({
        ...baseUpdate,
        modelYearMin: lookedUpYear ?? value.modelYearMin,
        modelYearMax: lookedUpYear ?? value.modelYearMax,
      });
    }
  };

  return (
    <div className="vehicle-enrichment">
      <style>{`
        .vehicle-enrichment .veh-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(min(220px, 100%), 1fr));
          gap: 12px;
        }

        @media (max-width: 480px) {
          .vehicle-enrichment .veh-grid {
            grid-template-columns: 1fr;
            gap: 16px;
          }
        }
      `}</style>

      <div className="veh-reg-panel">
        <div className="veh-reg-controls">
          <Search
            placeholder={t('vehicleEnrichment.registrationPlaceholder')}
            enterButton={t('vehicleEnrichment.lookup')}
            size="large"
            value={reg}
            onChange={e => setReg(e.target.value.toUpperCase())}
            onSearch={onLookup}
            loading={isFetching}
            style={{ marginBottom: 16 }}
            maxLength={10}
          />
        </div>
      </div>

      <div className="veh-grid veh-row">
        <div translate="no" lang="en">
          <Text type="secondary">{t('vehicleEnrichment.manufacturer')}</Text>
          <Select
            id="vf-mfr"
            showSearch
            placeholder={t('vehicleEnrichment.selectManufacturer')}
            value={value.manufacturer}
            onChange={v => setField('manufacturer', v)}
            options={manufacturers.map(m => ({ value: m, label: m }))}
            style={{ width: '100%' }}
            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            allowClear
          />
        </div>
        <div translate="no" lang="en">
          <Text type="secondary">{t('vehicleEnrichment.modelSeries')}</Text>
          <Select
            id="vf-mdl"
            showSearch
            placeholder={t('vehicleEnrichment.selectModelSeries')}
            value={value.modelSeries}
            onChange={v => setField('modelSeries', v)}
            options={modelSeries.map(m => ({ value: m, label: m }))}
            style={{ width: '100%' }}
            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            disabled={!value.manufacturer}
            allowClear
          />
        </div>
        <div translate="no" lang="en">
          <Text type="secondary">{t('vehicleEnrichment.engineType')}</Text>
          <Select
            id="vf-eng"
            showSearch
            placeholder={t('vehicleEnrichment.selectEngineType')}
            value={value.engineBaseType}
            onChange={v => setField('engineBaseType', v)}
            options={engineTypes.map(m => ({ value: m, label: m }))}
            style={{ width: '100%' }}
            filterOption={(input, option) => (option?.label as string).toLowerCase().includes(input.toLowerCase())}
            allowClear
          />
        </div>
        {yearMode === 'single' ? (
          <div>
            <Text type="secondary">{t('vehicleEnrichment.modelYear')}</Text>
            <InputNumber
              style={{ width: '100%' }}
              value={value.modelYear}
              min={1900}
              max={2035}
              onChange={v => setField('modelYear', v ?? undefined)}
              placeholder={t('vehicleEnrichment.modelYearPlaceholder')}
            />
          </div>
        ) : (
          <div>
            <Text type="secondary">Modellår (Min-Max)</Text>
            <div style={{ display: 'flex', gap: 8 }}>
              <InputNumber
                style={{ width: '50%' }}
                value={value.modelYearMin}
                min={1900}
                max={2035}
                onChange={v => setField('modelYearMin', v ?? undefined)}
                placeholder="Min"
              />
              <InputNumber
                style={{ width: '50%' }}
                value={value.modelYearMax}
                min={1900}
                max={2035}
                onChange={v => setField('modelYearMax', v ?? undefined)}
                placeholder="Max"
              />
            </div>
          </div>
        )}
      </div>

      <div className="veh-row" style={{ marginTop: 12 }}>
        <Text type="secondary">{t('vehicleEnrichment.eurocode')}</Text>
        <Input
          placeholder={t('vehicleEnrichment.eurocodePlaceholder')}
          value={value.euroCode ?? ''}
          onChange={e => setField('euroCode', e.target.value || undefined)}
          allowClear
        />
      </div>
    </div>
  );
};


